import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  DollarSign,
  Target,
  Bell,
  ArrowRight,
  CreditCard,
  Calculator,
  Briefcase,
  Map,
  BookOpen,
  Home,
  LogIn,
  Settings,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ROUTE_PATHS } from '@/lib/index';
import { creditScores, creditScoreHistory, taxSavingsBreakdown } from '@/lib/dashboard-data';
import { springPresets } from '@/lib/motion';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export default function Dashboard() {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('6m');
  const middleScore = 715; // Middle of the three bureau scores
  const taxSavingsFound = 47200;
  const fundingEligibility = 85;
  const goalProgress = 68;

  const modules = [
    {
      id: 'credit-repair',
      title: 'Credit Repair',
      icon: CreditCard,
      path: ROUTE_PATHS.CREDIT_REPAIR,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'Dispute items, generate letters, boost your score',
    },
    {
      id: 'tax-strategy',
      title: 'Tax Strategy',
      icon: Calculator,
      path: ROUTE_PATHS.TAX_STRATEGY,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      description: 'Find deductions, optimize entity, save thousands',
    },
    {
      id: 'business-funding',
      title: 'Business Funding',
      icon: Briefcase,
      path: ROUTE_PATHS.BUSINESS_FUNDING,
      color: 'text-chart-3',
      bgColor: 'bg-chart-3/10',
      description: 'Get matched with funding, build business credit',
    },
    {
      id: 'goal-roadmap',
      title: 'Goal Roadmap',
      icon: Map,
      path: ROUTE_PATHS.GOAL_ROADMAP,
      color: 'text-chart-4',
      bgColor: 'bg-chart-4/10',
      description: 'Step-by-step plans for house, car, funding',
    },
    {
      id: 'credit-education',
      title: 'Credit Education',
      icon: BookOpen,
      path: ROUTE_PATHS.CREDIT_EDUCATION,
      color: 'text-chart-5',
      bgColor: 'bg-chart-5/10',
      description: 'Learn credit hacks, strategies, and insider tips',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <span className="text-white font-bold text-xs">ESA</span>
                </div>
                <span className="text-sm font-medium hidden sm:block">Elevation Systems AI</span>
              </Link>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <div>
                <h1 className="text-xl font-bold">My Dashboard</h1>
                <p className="text-xs text-muted-foreground">
                  {new Date().toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-2 hidden sm:flex" onClick={() => navigate('/admin')}>
                <Settings className="w-4 h-4" />
                Admin
              </Button>
              <Button variant="outline" size="icon" className="relative">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive rounded-full text-[10px] flex items-center justify-center text-white">3</span>
              </Button>
              <Button variant="ghost" size="sm" className="gap-2" onClick={() => navigate('/')}>
                <Home className="w-4 h-4" />
                <span className="hidden sm:block">Home</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Banner */}
      <div className="bg-primary/10 border-b border-primary/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <p className="text-sm font-medium">
              <span className="text-primary">Action Required:</span> Review and send 2 dispute letters to Equifax
            </p>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => navigate('/dashboard/credit-repair')}>
              View <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* KPI Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
          >
            <Card className="p-6 bg-primary/10 border-primary/20">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>
                <Badge variant="outline" className="border-primary text-primary">
                  +139 pts
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Middle Credit Score</p>
                <p className="text-4xl font-bold font-mono text-primary">{middleScore}</p>
                <p className="text-xs text-muted-foreground">From 576 (8 months ago)</p>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
          >
            <Card className="p-6 bg-accent/10 border-accent/20">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-accent" />
                </div>
                <Badge variant="outline" className="border-accent text-accent">
                  Unclaimed
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Tax Savings Found</p>
                <p className="text-4xl font-bold font-mono text-accent">${(taxSavingsFound / 1000).toFixed(1)}k</p>
                <p className="text-xs text-muted-foreground">AI-detected deductions</p>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-foreground" />
                </div>
                <Badge variant="outline">{fundingEligibility}%</Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Funding Eligibility</p>
                <p className="text-4xl font-bold font-mono">${(fundingEligibility * 1000).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Estimated approval amount</p>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.3 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Target className="w-6 h-6 text-foreground" />
                </div>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Goal Progress</p>
                <p className="text-4xl font-bold font-mono">{goalProgress}%</p>
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Buy a House</span>
                    <span className="font-mono text-primary">Phase 3 of 5</span>
                  </div>
                  <Progress value={goalProgress} className="h-2" />
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Credit Score History */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Credit Score Journey</h3>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={selectedPeriod === '6m' ? 'default' : 'outline'}
                  onClick={() => setSelectedPeriod('6m')}
                >
                  6M
                </Button>
                <Button
                  size="sm"
                  variant={selectedPeriod === '1y' ? 'default' : 'outline'}
                  onClick={() => setSelectedPeriod('1y')}
                >
                  1Y
                </Button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={creditScoreHistory}>
                <defs>
                  <linearGradient id="colorEquifax" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorExperian" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorTransunion" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.22 0.028 240)" />
                <XAxis dataKey="month" stroke="oklch(0.56 0.015 240)" tick={{ fontSize: 11 }} />
                <YAxis domain={[540, 800]} stroke="oklch(0.56 0.015 240)" width={45} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'oklch(0.13 0.022 240)',
                    border: '1px solid oklch(0.22 0.028 240)',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="equifax"
                  stroke="#10b981"
                  fillOpacity={1}
                  fill="url(#colorEquifax)"
                  name="Equifax"
                />
                <Area
                  type="monotone"
                  dataKey="experian"
                  stroke="#3b82f6"
                  fillOpacity={1}
                  fill="url(#colorExperian)"
                  name="Experian"
                />
                <Area
                  type="monotone"
                  dataKey="transunion"
                  stroke="#8b5cf6"
                  fillOpacity={1}
                  fill="url(#colorTransunion)"
                  name="TransUnion"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* Tax Savings Breakdown */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-6">Tax Savings Breakdown</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={taxSavingsBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.22 0.028 240)" />
                <XAxis dataKey="category" stroke="oklch(0.56 0.015 240)" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" height={55} />
                <YAxis stroke="oklch(0.56 0.015 240)" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'oklch(0.13 0.022 240)',
                    border: '1px solid oklch(0.22 0.028 240)',
                    borderRadius: '8px',
                  }}
                  formatter={(value) => `$${value.toLocaleString()}`}
                />
                <Bar dataKey="amount" fill="oklch(0.74 0.18 85)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* 3 Bureau Scores */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-6">Current Credit Scores</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {creditScores.map((bureau, index) => (
              <motion.div
                key={bureau.bureau}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{bureau.bureau}</span>
                  <Badge variant="outline" style={{ color: bureau.color, borderColor: bureau.color }}>
                    +{bureau.score - bureau.previousScore}
                  </Badge>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold font-mono mb-2" style={{ color: bureau.color }}>
                    {bureau.score}
                  </div>
                  <div className="text-sm text-muted-foreground">from {bureau.previousScore}</div>
                </div>
                <Progress value={((bureau.score - 300) / 550) * 100} className="h-3" style={{ backgroundColor: `${bureau.color}20` }} />
              </motion.div>
            ))}
          </div>
        </Card>

        {/* Quick Actions / Modules */}
        <div>
          <h3 className="text-2xl font-bold mb-6">Your Financial Modules</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => {
              const Icon = module.icon;
              return (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                  whileHover={{ y: -6 }}
                >
                  <Link to={module.path}>
                    <Card className="p-6 h-full hover:border-primary/50 transition-all cursor-pointer group">
                      <div className={`w-14 h-14 rounded-2xl ${module.bgColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className={`w-7 h-7 ${module.color}`} />
                      </div>
                      <h4 className="text-lg font-semibold mb-2">{module.title}</h4>
                      <p className="text-sm text-muted-foreground mb-4">{module.description}</p>
                      <div className="flex items-center text-sm font-medium text-primary group-hover:gap-2 transition-all">
                        Explore Module <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
